<script setup lang="ts">
import { computed, useId } from "vue";

/**
 * O envelope de um campo: rótulo, obrigatório, dica, erro — e a ligação entre
 * eles.
 *
 * O desenho não é novo: ele estava certo e PRESO dentro de `DynamicForm`, que
 * era o único lugar do produto onde dica e erro nasciam do mesmo lugar que o
 * rótulo. Aqui ele ganha o que só o `TextField` tinha — um id de verdade, `for`,
 * `aria-invalid` e `aria-describedby` — e passa a valer para todo campo.
 *
 * Dica e erro só ocupam altura quando existem. Um campo válido não pode ser mais
 * alto que o vizinho só porque um dia pode dar errado.
 */
const props = defineProps<{
	label?: string;
	/** Texto de apoio, abaixo do controle. */
	hint?: string;
	/** A recusa, já em português. Vazio é o mesmo que ausente. */
	error?: string;
	required?: boolean;
	/** O id do controle, quando quem chama já tem um. */
	id?: string;
	/**
	 * O controle está inválido embora a mensagem more em outro lugar.
	 *
	 * É o caso de um widget dentro deste envelope: quem desenha o erro é o
	 * envelope, mas quem tem de anunciar `aria-invalid` é o controle.
	 */
	invalid?: boolean;
	/** Ids de descrição que vêm de fora, somados aos daqui. */
	describedBy?: string;
}>();

const generated = useId();
const controlId = computed(() => props.id ?? generated);
const hintId = computed(() => `${controlId.value}-hint`);
const errorId = computed(() => `${controlId.value}-error`);
const wrong = computed(() => props.invalid || Boolean(props.error));

/**
 * O que se lê depois do rótulo: a dica e a recusa, nesta ordem. Vazio vira
 * `undefined` — um `aria-describedby` apontando para nada é pior que nenhum.
 */
const describes = computed(() => {
	const ids = [
		props.describedBy,
		props.hint ? hintId.value : null,
		props.error ? errorId.value : null,
	].filter((id): id is string => Boolean(id));
	return ids.length > 0 ? ids.join(" ") : undefined;
});
</script>

<template>
	<div class="space-y-1.5">
		<label v-if="label" :for="controlId" class="block text-xs font-medium text-fg-muted">
			{{ label }}
			<span v-if="required" class="text-danger">*</span>
		</label>

		<slot :id="controlId" :described-by="describes" :invalid="wrong" />

		<p v-if="hint" :id="hintId" class="text-xs text-fg-subtle">{{ hint }}</p>
		<p v-if="error" :id="errorId" class="text-xs text-danger">{{ error }}</p>
	</div>
</template>
